import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck, User, Lock, ArrowRight } from 'lucide-react';
import Logo from '../components/common/Logo';
import { appUrl, publicUrl } from '../utils/routing';

const PROFESSIONAL_NOTICE = 'O Laudando é uma ferramenta para profissionais de saúde elaborarem e emitirem seus próprios laudos. Não solicita, intermedeia ou fornece laudos ao público.';

const PAGES = {
  '/sobre': {
    icon: User,
    title: 'Sobre o Laudando | Plataforma profissional de laudos',
    heading: 'Sobre o Laudando',
    description: 'O Laudando é uma plataforma para profissionais de saúde elaborarem, organizarem e emitirem seus próprios laudos com um fluxo simples e rastreável.',
    lead: 'O Laudando nasceu da rotina real de profissionais de saúde que precisavam produzir documentos com mais consistência, velocidade e organização, sem transformar a plataforma em intermediadora de laudos.',
    sections: [
      ['O que é o Laudando', 'Uma ferramenta de trabalho para médicos, veterinários e outros profissionais habilitados elaborarem seus próprios laudos. Os modelos são pontos de partida editáveis; a interpretação, revisão e emissão permanecem sob responsabilidade do profissional.'],
      ['Responsabilidade', 'Responsável legal e médico: Carlos Felipe — CRM/MG 105.721. A identificação profissional é pública para deixar claro quem responde pelo produto e pela comunicação institucional.'],
      ['Origem do produto', 'O produto nasceu da experiência de um médico e uma veterinária com a necessidade de tornar a emissão de laudos mais simples, organizada e agradável no uso diário.'],
      ['O que não fazemos', PROFESSIONAL_NOTICE],
    ],
  },
  '/seguranca': {
    icon: ShieldCheck,
    title: 'Segurança e rastreabilidade | Laudando',
    heading: 'Segurança e rastreabilidade',
    description: 'Como o Laudando trata acesso, integridade de documentos, validação pública e analytics sem conteúdo clínico.',
    lead: 'A segurança do Laudando é desenhada para separar conta, documento clínico e telemetria. Esta página descreve apenas mecanismos que existem no produto hoje.',
    sections: [
      ['Acesso e isolamento', 'O aplicativo exige autenticação para as áreas profissionais. Laudos e recursos privados são associados ao usuário da conta, e rotas com conteúdo de laudo não são destinadas à indexação por mecanismos de busca.'],
      ['Integridade do laudo', 'Ao emitir um laudo, o sistema registra hash SHA-256 do documento, token de acesso e carimbo de tempo, permitindo verificar integridade e rastreabilidade da versão emitida.'],
      ['Validação pública', 'O Laudando oferece uma página pública de validação do documento. Essa página serve para conferir a versão registrada e seus dados de integridade; ela não transforma o Laudando em emissor do laudo.'],
      ['Assinatura digital', 'O Laudando não emite assinatura digital ICP-Brasil. Hash, carimbo de tempo e página de validação são mecanismos de integridade e rastreabilidade e não devem ser apresentados como certificado ICP-Brasil.'],
      ['Analytics', 'Em rotas sensíveis, o produto desativa autocaptura e gravação de sessão. Eventos de produto removem campos de paciente, texto clínico, diagnóstico, conclusão, e-mail, nome, telefone, endereço e conteúdo bruto.'],
    ],
  },
  '/privacidade': {
    icon: Lock,
    title: 'Privacidade | Laudando',
    heading: 'Privacidade no Laudando',
    description: 'Princípios de privacidade do Laudando para conta profissional, documentos clínicos, modelos públicos e analytics.',
    lead: 'O princípio central é simples: dados clínicos existem para viabilizar o trabalho do profissional e não para alimentar marketing, páginas SEO ou telemetria comportamental.',
    sections: [
      ['Dados da conta', 'O serviço utiliza dados de conta, perfil profissional e cobrança quando necessários para autenticação, acesso ao produto e gestão do plano.'],
      ['Conteúdo clínico', 'Dados de paciente e conteúdo do laudo pertencem ao fluxo privado do profissional. Esses campos não são enviados intencionalmente aos eventos do PostHog usados para medir aquisição, ativação e conversão.'],
      ['Modelos públicos', 'Um modelo só é elegível para páginas públicas quando está marcado como público, aprovado para SEO e sanitizado. Modelos privados ou pendentes não entram no sitemap nem nas páginas indexáveis.'],
      ['Analytics e aquisição', 'Atribuição usa parâmetros como UTM, origem da landing e identificadores técnicos do modelo. Campos de paciente, diagnóstico, conclusão, texto clínico e dados pessoais diretos são bloqueados da camada de analytics.'],
      ['Uso profissional', PROFESSIONAL_NOTICE],
    ],
  },
};

const setPageMeta = (page, pathname) => {
  document.title = page.title;
  const description = document.head.querySelector('meta[name="description"]') || document.head.appendChild(document.createElement('meta'));
  description.setAttribute('name', 'description');
  description.setAttribute('content', page.description);
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', publicUrl(pathname));
};

export default function TrustPage() {
  const location = useLocation();
  const page = PAGES[location.pathname] || PAGES['/sobre'];

  useEffect(() => {
    setPageMeta(page, location.pathname);
  }, [location.pathname, page]);

  return (
    <div className="min-h-screen bg-paper-alt">
      <header className="bg-paper border-b border-line">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
          <Link to="/" aria-label="Laudando, início"><Logo markClassName="h-10 w-10" /></Link>
          <nav className="flex items-center gap-4 text-sm font-semibold">
            <Link to="/modelos-laudo" className="text-muted hover:text-navy">Modelos</Link>
            <a href={appUrl('/login')} className="text-navy hover:underline">Entrar</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-ink mb-5">{page.heading}</h1>
          <p className="text-lg text-muted leading-8 max-w-3xl">{page.lead}</p>
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid grid-cols-1 md:grid-cols-2 gap-5">
          {page.sections.map(([heading, text]) => (
            <article key={heading} className="bg-paper border border-line rounded-card p-6 shadow-card">
              <h2 className="font-display text-xl font-bold text-ink mb-3">{heading}</h2>
              <p className="text-sm md:text-base text-muted leading-7">{text}</p>
            </article>
          ))}
        </section>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-navy text-white rounded-card p-7 md:p-9 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div><h2 className="font-display text-2xl font-bold">Veja um modelo antes de criar sua conta.</h2><p className="text-white/70 mt-2">Pré-visualize um laudo, escolha o modelo e abra diretamente no editor.</p></div>
            <Link to="/modelos-laudo" className="btn-convert whitespace-nowrap">Ver modelos <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-line bg-paper">
        <div className="max-w-5xl mx-auto px-4 py-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted">
          <Link to="/sobre" className="hover:text-navy">Sobre</Link>
          <Link to="/seguranca" className="hover:text-navy">Segurança</Link>
          <Link to="/privacidade" className="hover:text-navy">Privacidade</Link>
          <Link to="/modelos-laudo" className="hover:text-navy">Modelos</Link>
        </div>
      </footer>
    </div>
  );
}